import { useEffect, useRef, useState } from "react";
import { DoorOpen, Sparkles, Palette, CookingPot, Info, Cog, Armchair } from "lucide-react";
import { useDims } from "@/lib/units";
import { asset } from "@/lib/api";
import { nearestSweep } from "./useMatterport";

const dist2 = (a, b) => (a.x - b.x) ** 2 + (a.y - b.y) ** 2 + (a.z - b.z) ** 2;

function visibleHere(h, pose, sweeps) {
  if (h.visible_from?.length) return h.visible_from.includes(pose.sweep);
  const cur = sweeps[pose.sweep];
  const homeId = h.pose?.sweep && sweeps[h.pose.sweep] ? h.pose.sweep : nearestSweep(sweeps, h.position);
  const home = sweeps[homeId];
  if (!cur || !home) return dist2(h.position, pose.position) < 9;
  if (pose.sweep === homeId) return true;
  const room = (s) => s?.roomInfo?.id;
  if (room(cur) && room(home)) return room(cur) === room(home);
  const floor = (s) => s?.floorInfo?.id ?? s?.floorInfo?.sequence;
  if (floor(cur) !== floor(home)) return false;
  const near = (home.neighbors || []).includes(pose.sweep) || (cur.neighbors || []).includes(homeId);
  return near && dist2(h.position, pose.position) < 16;
}

let rayWarned = false;
const blocked = async (sdk, h, p, cam) => {
  if (h.visible_from?.length) return false;
  const d = await sdk.Renderer.getWorldPositionData({ x: p.x, y: p.y }).catch((e) => { if (!rayWarned) { rayWarned = true; console.warn("[tour] line-of-sight check unavailable", e); } return null; });
  if (!d?.position) return false;
  const dh = Math.sqrt(dist2(h.position, cam));
  return Math.sqrt(dist2(d.position, cam)) < dh - Math.max(0.35, dh * 0.08);
};

export const KIND_ICON = { room: DoorOpen, spec: Info, finish: Palette, appliance: CookingPot, part: Cog, area: Armchair };
const PIN_SIZE = { sm: "w-9 h-9", md: "w-11 h-11", lg: "w-14 h-14" };
const PIN_SHAPE = { circle: "rounded-full", square: "rounded-lg", drop: "rounded-full rounded-br-none rotate-45" };

export function swatchFor(h, choices, finishes) {
  if (!h.finish_category_id) return null;
  const cat = finishes.find((c) => c.id === h.finish_category_id);
  return cat?.options.find((o) => o.id === choices[cat.id]) || null;
}

export function HealthDot({ h, className = "" }) {
  const bad = h.health === "needs";
  return (
    <span title={`${bad ? "Needs attention" : "OK"} at last inspection${h.health_date ? ` (${new Date(h.health_date).toLocaleDateString("en-CA")})` : ""}`}
      className={`${className} rounded-full border-2 border-white shadow ${bad ? "bg-red-500 animate-pulse" : "bg-emerald-500"}`} data-testid={`health-dot-${h.id}`} />
  );
}

export function HotspotLayer({ sdk, iframeRef, sweepsRef, hotspots, choices, finishes, onOpen, logo = "", pin = {} }) {
  const dims = useDims();
  const [pts, setPts] = useState({});
  const [seen, setSeen] = useState({});
  const timer = useRef(null);
  useEffect(() => {
    if (!sdk) return undefined;
    const placed = hotspots.filter((h) => h.position);
    const byId = Object.fromEntries(placed.map((h) => [h.id, h]));
    const canRay = !!sdk.Renderer?.getWorldPositionData;
    const check = async (next, pose) => {
      const res = await Promise.all(Object.entries(next).map(async ([id, p]) => [id, !(await blocked(sdk, byId[id], p, pose.position))]));
      setSeen(Object.fromEntries(res));
    };
    const sub = sdk.Camera.pose.subscribe((pose) => {
      const el = iframeRef.current;
      if (!el) return;
      const size = { w: el.clientWidth, h: el.clientHeight };
      const next = {};
      const sweeps = sweepsRef?.current || {};
      const inside = !pose.mode || String(pose.mode).includes("inside");
      placed.forEach((h) => {
        if (!inside || !visibleHere(h, pose, sweeps)) return;
        const s = sdk.Conversion.worldToScreen(h.position, pose, size);
        if (s && s.z <= 1 && s.x >= 0 && s.y >= 0 && s.x <= size.w && s.y <= size.h) next[h.id] = { x: s.x, y: s.y };
      });
      setPts(next);
      if (canRay) {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => check(next, pose), 120);
      } else setSeen(Object.fromEntries(Object.keys(next).map((id) => [id, true])));
    });
    return () => { clearTimeout(timer.current); sub?.cancel?.(); };
  }, [sdk, hotspots, iframeRef, sweepsRef]);

  return (
    <div className="absolute inset-0 pointer-events-none z-10" data-testid="hotspot-layer">
      {hotspots.filter((h) => pts[h.id] && seen[h.id]).map((h) => {
        const Icon = KIND_ICON[h.kind] || Info;
        const sw = swatchFor(h, choices, finishes);
        return (
          <button
            key={h.id}
            onClick={() => onOpen(h)}
            className="absolute pointer-events-auto -translate-x-1/2 -translate-y-1/2 group flex items-center gap-2"
            style={{ left: pts[h.id].x, top: pts[h.id].y }}
            data-testid={`hotspot-marker-${h.id}`}
          >
            {(h.photos?.[0] || h.description) && (
              <span className="hidden md:group-hover:block absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-60 rounded-2xl overflow-hidden glass-dark text-left shadow-2xl pointer-events-none rise" data-testid={`hotspot-card-${h.id}`}>
                {h.photos?.[0] && <img src={asset(h.photos[0])} alt="" className="w-full aspect-[16/10] object-cover" />}
                <span className="block p-3">
                  <span className="block font-semibold text-sm">{h.title}</span>
                  {hotspots.some((c) => c.parent_id === h.id) && <span className="block text-[11px] text-white/70 mt-0.5">{hotspots.filter((c) => c.parent_id === h.id).map((c) => c.title).join(" · ")}</span>}
                  {h.description && <span className="block text-xs text-white/70 mt-1 line-clamp-2">{h.description}</span>}
                  <span className="block text-[11px] text-ba mt-2 font-semibold">Click to explore</span>
                </span>
              </span>
            )}
            <span className={`sk-keep relative ${PIN_SIZE[pin.size] || PIN_SIZE.md} ${PIN_SHAPE[pin.shape] || PIN_SHAPE.circle} shadow-xl grid place-items-center ring-4 ring-white/30 group-hover:scale-110 transition-transform ${h.kind === "room" || h.kind === "area" || h.kind === "part" ? "pin-pulse" : ""}`} style={{ background: pin.color || "rgba(255,255,255,.95)" }}>
              <span className={`grid place-items-center ${pin.shape === "drop" ? "-rotate-45" : ""}`}>
              {logo ? <img src={asset(logo)} alt="" className="w-[62%] h-[62%] min-w-6 min-h-6 object-contain" /> : sw ? <span className="w-7 h-7 rounded-full border" style={{ background: sw.swatch }} /> : <Icon className="w-5 h-5 text-bp" />}
              </span>
              {logo && sw && <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-white shadow" style={{ background: sw.swatch }} data-testid={`hotspot-swatch-${h.id}`} />}
              {h.upgrade_available && <Sparkles className="absolute -top-1 -right-1 w-4 h-4 text-ba" />}
              {h.health && <HealthDot h={h} className="absolute -top-1 -right-1 w-4 h-4" />}
            </span>
            {(pin.labels === "all" || ((pin.labels || "rooms") === "rooms" && (h.kind === "room" || h.kind === "part" || h.kind === "area"))) && (
              <span className="glass-dark rounded-full px-3 py-1 text-xs whitespace-nowrap">
                {h.title}{h.dimensions ? ` · ${dims(h.dimensions)}` : ""}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
